import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useTheme } from '@/contexts/ThemeContext';

export const Footer = () => {
  const { theme } = useTheme();
  
  const logoSrc = theme === 'light' ? '/logo-light' : '/logo-dark';
    
  return (
    <footer className="relative mt-24">
      {/* Gradient overlay for seamless transition - extends higher for smoother blend */}
      <div className="absolute inset-x-0 -top-12 h-32 bg-gradient-to-b from-transparent via-transparent to-gray-50/30 dark:to-[hsl(220,18%,8%)]/30 pointer-events-none" />
      
      {/* Main footer content with transparent background */}
      <div className="relative bg-gradient-to-t from-gray-100/60 to-gray-50/30 dark:from-[hsl(220,18%,10%)]/60 dark:to-transparent backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="space-y-4">
              <Link to="/" className="flex items-center">
                <picture>
                  <source srcSet={`${logoSrc}.webp`} type="image/webp" />
                  <img 
                    src={`${logoSrc}.png`}
                    alt="NeoBroker Logo" 
                    width="160"
                    height="32"
                    className="h-8 w-auto"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </Link>
              <p className="text-sm text-muted-foreground">
                Find the perfect broker for your trading needs. Compare fees, features, and more.
              </p>
            </div>
            
            {/* Quick Links */}
            <div>
              <h3 className="font-semibold text-foreground mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/compare" className="text-muted-foreground hover:text-foreground transition-colors">
                    Compare Brokers
                  </Link>
                </li>
                <li>
                  <Link to="/trading-guide" className="text-muted-foreground hover:text-foreground transition-colors">
                    Trading Guide
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="/widgets" className="text-muted-foreground hover:text-foreground transition-colors">
                    Widgets
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h3 className="font-semibold text-foreground mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/imprint" className="text-muted-foreground hover:text-foreground transition-colors">
                    Imprint
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h3 className="font-semibold text-foreground mb-4">Contact</h3>
              <a 
                href="mailto:info@exagoramedia.com" 
                className="inline-flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>info@exagoramedia.com</span>
              </a>
            </div>
          </div>
          
          <Separator className="my-8 bg-gray-200/50 dark:bg-white/10" />
          
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} NeoBroker. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                Terms of Service
              </Link>
              <Link to="/imprint" className="text-muted-foreground hover:text-foreground transition-colors">
                Imprint
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
